import { getType } from "mime";

export const calcDistance = (currentPos, newPercentage, totalDistance) => {
  if (currentPos < 0) {
    const nP = (100 - newPercentage) / 100;
    const newPos = totalDistance * nP;

    return newPos * -1;
  } else {
    const nP = newPercentage / 100;
    const newPos = totalDistance * nP;
    return newPos - (currentPos === totalDistance ? totalDistance : 0);
  }
};

export const toPixel = (num, total) => (num / 100) * total;

export const calcCircumference = radius => 2 * Math.PI * radius;

export const degreeToRadian = degree => (degree * Math.PI) / 180;

export const radianToDegree = radians => (radians * 180) / Math.PI;

export const calcCircleX = (radius, angle) =>
  radius * parseFloat(Math.cos(degreeToRadian(angle)).toFixed(3));

export const calcCircleY = (radius, angle) =>
  radius * parseFloat(Math.sin(degreeToRadian(angle)).toFixed(3));

export const mod = (n, m) => {
  return ((n % m) + m) % m;
};

const componentToHex = c => {
  const hex = Math.round(c).toString(16);
  return hex.length === 1 ? '0' + hex : hex;
};

export const valueToHex = val => {
  const hex = componentToHex(val);
  return parseInt(hex + hex + hex, 16);
};

export const perpendicular = type => {
  return type === 'horizontal' ? 'vertical' : 'horizontal';
};

export const isDefined = value => value !== undefined && value !== null;

export const borderLines = (inset, type) => {
  let inset1, inset2;

  if (isDefined(inset)) {
    inset1 = inset;
    inset2 = !inset;

    if (type === 'vertical') {
      inset1 = !inset;
      inset2 = inset;
    }
  }

  return [
    {
      startPosition: 0,
      drawFromPosition: 0,
      drawToPosition: 100,
      drawFull: true,
      inset: inset1,
      name: type === 'horizontal' ? 'Top Border' : 'Left Border',
    },
    {
      startPosition: 100,
      drawFromPosition: 0,
      drawToPosition: 100,
      drawFull: true,
      inset: inset2,
      name: type === 'horizontal' ? 'Bottom Border' : 'Right Border',
    },
  ];
};

export const findClosest = (arr, target) => {
  return arr.reduce((a, b) => {
    return Math.abs(b - target) < Math.abs(a - target) ? b : a;
  });
};

// top, left, bottom, right, center, top-left, top-right, bottom-left, bottom-right
// positions should have top, left, right, center - {x, y}
const getGravity = (gravity, positions) => {
  const values = gravity.split('-');
  let x, y;

  if (values.length === 1) {
    const [single] = values;

    if (single === 'top' || 'bottom') {
      x = positions.center.x;
      y = positions[single];
    }
    if (single === 'left' || 'right') {
      x = positions[single];
      y = positions.center.y;
    }
  } else {
    const [_y, _x] = values;
    x = positions[_x];
    y = positions[_y];
  }
  return { x, y };
};

export const calculateFit = (target, container, cover) => {
    const containerW = container.width;
    const containerH = container.height;
    const targetW = target.width;
    const targetH = target.height;

    const rw = containerW / targetW;
    const rh = containerH / targetH;
    let r;

    if (cover) {
      r = rw > rh ? rw : rh;
    } else {
      r = rw < rh ? rw : rh;
    }

    return {
      left: (containerW - targetW * r) >> 1,
      top: (containerH - targetH * r) >> 1,
      width: targetW * r,
      height: targetH * r,
      scale: r,
    };
};

//α = arcsin(a / c)
const calcAngle = (a, c) => {
  return 90 - radianToDegree(Math.asin(a / c));
};

export const findCircleLineIntersections = (
  circleX,
  circleY,
  radius,
  x1,
  y1,
  x2,
  y2,
) => {
  // calculate distance from circle center to line - side one
  // calculate angle
  // find third side of triangle using Pythagorean's Theorem
  //    side two is the radius of the circle
  // use the length of that side to find missing coordinate of circle intersection

  // vertical
  if (x1 === x2) {
    if (circleX === x1) {
      return [
        { x: x1, y: circleY + radius, angle: 90 },
        { x: x1, y: circleY - radius, angle: 90 },
      ];
    }

    const a = Math.abs(circleX - x1);
    const c = radius;
    const angle = calcAngle(a, c);

    if (a > c) return [];

    const b = Math.sqrt(Math.pow(c, 2) - Math.pow(a, 2));

    return [
      { x: x1, y: circleY + b, angle },
      { x: x1, y: circleY - b, angle },
    ];
  }

  // horizontal
  if (y1 === y2) {
    if (circleX === x1) {
      return [
        { x: circleX + radius, y: y1, angle: 90 },
        { x: circleX - radius, y: y1, angle: 90 },
      ];
    }

    const a = Math.abs(circleY - y1);
    const c = radius;
    const angle = calcAngle(a, c);

    if (a > c) return [];

    const b = Math.sqrt(Math.pow(c, 2) - Math.pow(a, 2));

    return [
      { x: circleX + b, y: y1, angle },
      { x: circleX - b, y: y1, angle },
    ];
  }
};

export const isVideo = url => {
  const ext = url.split('.').pop().split('?')[0]
  const mimeType = getType(ext);
  return mimeType.startsWith('video');
}

export const preloadVideo = (element, src) => {
  document.body.appendChild(element);
  element.style.position = 'absolute';
  element.style.visibility = 'hidden';
  element.importance = "high";
  return new Promise(resolve => {
    const xhrReq = new XMLHttpRequest();
    xhrReq.open('GET', src, true);
    xhrReq.responseType = 'blob';

    xhrReq.onload = function() {
      if (this.status === 200) {
        element.src = URL.createObjectURL(this.response);
        element.autoplay = false;
        element.muted = true;
        console.log(src, ' - preloaded');
      }
    };

    element.oncanplay = () => {
      resolve(element.src);
    };

    xhrReq.onerror = function() {
      console.log('err', arguments);
    };

    xhrReq.onprogress = function(e) {
      if (e.lengthComputable) {
        const percentComplete = (((e.loaded / e.total) * 100) | 0) + '%';
        console.log(src, '- progress: ', percentComplete);
      }
    };

    xhrReq.send();
  });
};
